import { z } from 'zod';
import { ACCEPT_ONLY_HARDWARE_FIELDS, isBlankHardwareLeaf, } from './bty-hardware.js';
import { CIRCUIT_BREAKER_FIELDS, normalizeCircuitBreakerSize } from './circuit-breaker-size.js';
import { FieldHardwareSchema } from './field.js';
export const HardwareKindSchema = z.enum([
    'inverters',
    'modules',
    'batteries',
    'acIsolators',
    'dcIsolators',
]);
export const HARDWARE_CHECK_KINDS = {
    inverters: {
        label: 'Inverter',
        prompts: ['pv-inverter-model-details', 'inverter-inverter-label-and-serial-number'],
        fields: {
            make: 'Make',
            model: 'Model',
            serial: 'Serial number',
            ipRating: 'IP rating',
            acPowerRatingW: 'AC rated power (W). If absent, rated apparent power VA/kVA is a reviewer-confirmed fallback; quote its original designation and units',
            acCurrentNominalA: 'AC nominal current (A). Only if nominal is absent, read maximum AC output current (I AC max); quote the maximum designation in printedText',
            dcVoltageMpptLowerV: 'MPPT minimum voltage (V)',
            dcVoltageMpptUpperV: 'MPPT maximum voltage (V)',
            dcVoltageMaximumV: 'Maximum DC voltage (V)',
            dcCurrentMaximumA: 'Maximum PV DC input current (A), not battery or short-circuit current. Sum explicitly listed simultaneous input ratings, quoting the full line',
            dcCurrentShortCircuitA: 'Maximum DC input short-circuit current (Isc or Isc PV), one printed total in amperes. Not maximum input current. Omit per-MPPT or per-input lists; do not sum them',
            galvanicallyIsolated: 'Galvanically isolated (true/false), only if explicitly stated',
        },
    },
    modules: {
        label: 'Panel',
        prompts: ['pv-module-model-details', 'pv-module-serial'],
        fields: {
            make: 'Make',
            model: 'Model',
            serials: 'Example panel serial number (one serial)',
            powerRatingW: 'Rated power per panel (W)',
            voltageOpenCircuitV: 'Open-circuit voltage (V)',
            currentShortCircuitA: 'Short-circuit current (A)',
        },
    },
    batteries: {
        label: 'Battery',
        prompts: [
            'battery-system-battery-system-label-and-serial-number',
            'battery-system-battery-modules-labels-and-serial-numbers',
        ],
        fields: {
            make: 'Make',
            model: 'Model',
            series: 'Series',
            manufacturer: 'Manufacturer',
            serial: 'Serial number',
            capacityKwh: 'Capacity of this battery (kWh), not a calculated system total',
        },
    },
    acIsolators: {
        label: 'AC isolator',
        prompts: [
            'pv-ac-isolator-rating',
            'pv-main-switch-inverter-supply',
            'isolators-ac-isolators-before-during-after',
        ],
        fields: { ratingA: 'AC current rating (A)' },
    },
    dcIsolators: {
        label: 'DC isolator',
        prompts: [
            'pv-dc-isolator-before',
            'pv-dc-isolator-during',
            'pv-dc-isolator-after',
            'pv-inverter-integrated-isolator',
            'isolators-dc-load-break-disconnectors',
        ],
        fields: {
            voltageV: 'DC voltage rating (V)',
            currentA: 'DC current rating (A) at the stated voltage and wiring configuration',
            polarised: 'Polarised (true/false), only if explicitly stated',
        },
    },
};
export const HardwareCheckReadingSchema = z
    .object({
    field: z.string().min(1).max(60),
    value: z.string().trim().min(1).max(160),
    printedText: z.string().trim().min(1).max(300),
    inputCurrentsA: z.array(z.number().positive().max(1000)).min(2).max(20).optional(),
})
    .strict();
export const HardwareCheckExtractionSchema = z
    .object({ readings: z.array(HardwareCheckReadingSchema).max(20), needsClearerPhoto: z.boolean() })
    .strict();
export const HardwarePhotoCropSchema = z
    .object({
    x: z.number().min(0).max(1),
    y: z.number().min(0).max(1),
    width: z.number().min(0.02).max(1),
    height: z.number().min(0.02).max(1),
})
    .strict()
    .refine((c) => c.x + c.width <= 1.000001 && c.y + c.height <= 1.000001, 'Crop must stay inside the photo');
export const HardwareCheckRequestSchema = z
    .object({
    kind: HardwareKindSchema,
    index: z.number().int().min(0).max(99),
    photoId: z.string().uuid(),
    hardware: FieldHardwareSchema,
    crop: HardwarePhotoCropSchema.optional(),
    /** Switchboard reads the two main-switch breaker sizes, not an equipment row. */
    surface: z.enum(['equipment', 'switchboard']).optional(),
})
    .strict();
export const HardwareCheckSchema = HardwareCheckRequestSchema.extend({
    id: z.string().regex(/^[a-f0-9]{64}$/),
    sourceRevision: z.string(),
    jobId: z.string(),
    photoHash: z.string(),
    photoUploadedAt: z.string(),
    extractorVersion: z.string(),
    createdAt: z.string(),
    requestedBy: z.string(),
    readings: z.array(HardwareCheckReadingSchema),
    needsClearerPhoto: z.boolean(),
    uncertainFields: z.array(z.string()).default([]),
});
export const DcIsolatorRatingFieldSchema = z.enum(['voltageV', 'currentA']);
/** Inverter spec-label readings that can fill one DC isolator row. */
export const INVERTER_TO_DC_ISOLATOR_FIELDS = {
    voltageV: 'dcVoltageMaximumV',
    currentA: 'dcCurrentShortCircuitA',
};
export const INVERTER_ISOLATOR_SOURCE_LABEL = 'from inverter spec label';
export const InverterDcIsolatorApplicationSchema = z.object({
    index: z.number().int().nonnegative(),
    fields: z.array(DcIsolatorRatingFieldSchema).min(1).max(2),
    expectedRow: z.record(z.string(), z.unknown()),
});
export const HardwareCheckSelectionSchema = z.object({
    kind: HardwareKindSchema,
    index: z.number().int().nonnegative(),
    id: z.string().regex(/^[a-f0-9]{64}$/),
    fields: z.array(z.string().max(60)).max(20),
    expectedRow: z.record(z.string(), z.unknown()),
    /** Inverter-label ratings copied onto one Integrated or blank DC isolator. */
    dcIsolator: InverterDcIsolatorApplicationSchema.optional(),
    surface: z.enum(['equipment', 'switchboard']).optional(),
});
export const HardwareCheckAuditSchema = z.object({
    id: z.string(),
    kind: HardwareKindSchema,
    index: z.number(),
    photoId: z.string(),
    photoHash: z.string(),
    sourceRevision: z.string(),
    extractorVersion: z.string(),
    readings: z.array(HardwareCheckReadingSchema),
    crop: HardwarePhotoCropSchema.optional(),
    reviewedBy: z.string(),
    reviewedAt: z.string(),
});
export function hardwareCheckRow(hardware, kind, index) {
    return hardware[kind][index];
}
export function hardwareReadingValue(field, value) {
    if (field === 'serials')
        return [value];
    if (['galvanicallyIsolated', 'polarised'].includes(field)) {
        if (!['true', 'false'].includes(value))
            throw new Error('Invalid yes/no reading');
        return value === 'true';
    }
    if (CIRCUIT_BREAKER_FIELDS.includes(field)) {
        const normalized = normalizeCircuitBreakerSize(value);
        if (!normalized.ok || !normalized.value)
            throw new Error('Invalid numeric reading');
        return normalized.value;
    }
    if (['make', 'model', 'serial', 'ipRating', 'series', 'manufacturer', 'inverterSerial'].includes(field))
        return value;
    if (!/^\d+(?:\.\d+)?$/.test(value) || !Number.isFinite(Number(value)))
        throw new Error('Invalid numeric reading');
    return Number(value);
}
/** Label-only Isc evidence belongs to the DC isolator offer, not the inverter row. */
export function applicableHardwareCheckFields(kind, surface) {
    if (surface === 'switchboard')
        return [...CIRCUIT_BREAKER_FIELDS];
    return Object.keys(HARDWARE_CHECK_KINDS[kind].fields).filter((field) => !(kind === 'inverters' && field === 'dcCurrentShortCircuitA'));
}
export function applyHardwareCheck(hardware, check, fields) {
    const next = FieldHardwareSchema.parse(hardware);
    const allowed = applicableHardwareCheckFields(check.kind, check.surface);
    if (check.surface === 'switchboard') {
        const record = next;
        for (const field of fields) {
            const reading = check.readings.find((item) => item.field === field);
            if (!reading || !allowed.includes(field))
                throw new Error('Invalid hardware reading');
            if (ACCEPT_ONLY_HARDWARE_FIELDS.has(field) && !isBlankHardwareLeaf(record[field]))
                throw new Error('Populated value kept');
            record[field] = hardwareReadingValue(field, reading.value);
        }
        return FieldHardwareSchema.parse(next);
    }
    const row = hardwareCheckRow(next, check.kind, check.index);
    if (!row)
        throw new Error('Hardware row no longer exists');
    for (const field of fields) {
        const reading = check.readings.find((r) => r.field === field);
        if (!reading || !allowed.includes(field))
            throw new Error('Invalid hardware reading');
        if (ACCEPT_ONLY_HARDWARE_FIELDS.has(field) && !isBlankHardwareLeaf(row[field]))
            throw new Error('Populated value kept');
        row[field] =
            field === 'serials'
                ? [
                    reading.value,
                    ...z
                        .array(z.string())
                        .catch([])
                        .parse(row.serials)
                        .slice(1)
                        .filter((s) => s !== reading.value),
                ]
                : hardwareReadingValue(field, reading.value);
    }
    return FieldHardwareSchema.parse(next);
}
/** Compare snapshots independently of JSON object key order. */
export function hardwareValuesEqual(a, b) {
    if (Object.is(a, b))
        return true;
    if (!a ||
        !b ||
        typeof a !== 'object' ||
        typeof b !== 'object' ||
        Array.isArray(a) !== Array.isArray(b))
        return false;
    const left = a;
    const right = b;
    const keys = Object.keys(left).filter((k) => left[k] !== undefined);
    return (keys.length === Object.keys(right).filter((k) => right[k] !== undefined).length &&
        keys.every((k) => hardwareValuesEqual(left[k], right[k])));
}
export const HARDWARE_CHECK_LABELS = {
    make: 'Make',
    model: 'Model',
    serial: 'Serial number',
    serials: 'Example panel serial',
    series: 'Series',
    manufacturer: 'Manufacturer',
    ipRating: 'IP rating',
    acPowerRatingW: 'AC power (W)',
    acCurrentNominalA: 'AC nominal current (A)',
    dcVoltageMpptLowerV: 'MPPT minimum (V)',
    dcVoltageMpptUpperV: 'MPPT maximum (V)',
    dcVoltageMaximumV: 'Maximum DC voltage (V)',
    dcCurrentMaximumA: 'Maximum DC current (A)',
    dcCurrentShortCircuitA: 'Maximum DC short-circuit current (Isc)',
    galvanicallyIsolated: 'Galvanically isolated',
    powerRatingW: 'Panel power (W)',
    voltageOpenCircuitV: 'Open-circuit voltage (V)',
    currentShortCircuitA: 'Short-circuit current (A)',
    capacityKwh: 'Capacity (kWh)',
    inverterSerial: 'Connected inverter serial',
    circuitBreakerSizeMainSwitchGrid: 'Main switch (grid) breaker',
    circuitBreakerSizeMainSwitchInverter: 'Main switch inverter/alternative breaker',
    ratingA: 'AC rating (A)',
    voltageV: 'DC voltage (V)',
    currentA: 'DC current (A)',
    polarised: 'Polarised',
};
/**
 * DC isolator voltage and current offered from an inverter specification label.
 *
 * Precedence:
 * 1. A value already stored on the isolator is kept. It is not ticked or overwritten.
 * 2. An explicit DC isolator label reading wins. Applied together, that reading is
 *    written first and the inverter offer does not replace it.
 * 3. Otherwise voltage is the inverter's maximum DC input voltage and current is its
 *    maximum DC short-circuit current (Isc).
 *
 * One inverter and one Integrated or blank isolator pair on their own. Several
 * inverters or several isolators are not paired until `target` is the reviewer's
 * choice. Rooftop, Adjacent to inverter, and Other receive nothing. Per-input Isc
 * is not summed; a missing or per-input Isc is not a current offer.
 */
export function dcIsolatorAcceptsInverterRatings(row) {
    return !!row && (row.location === undefined || row.location === 'Integrated');
}
export function inverterIsolatorChoices(hardware) {
    return hardware.dcIsolators.flatMap((row, index) => dcIsolatorAcceptsInverterRatings(row) ? [index] : []);
}
/** Only the unambiguous one-inverter, one-eligible-isolator case. */
export function automaticInverterIsolatorIndex(hardware) {
    if (hardware.inverters.length !== 1 || hardware.dcIsolators.length !== 1)
        return undefined;
    return dcIsolatorAcceptsInverterRatings(hardware.dcIsolators[0]) ? 0 : undefined;
}
export function resolvedInverterIsolatorIndex(hardware, check, target) {
    if (check.kind !== 'inverters')
        return undefined;
    const automatic = automaticInverterIsolatorIndex(hardware);
    if (automatic !== undefined)
        return automatic;
    if (target === undefined || !inverterIsolatorChoices(hardware).includes(target))
        return undefined;
    return target;
}
export function inverterIsolatorReading(check, field) {
    const sourceField = INVERTER_TO_DC_ISOLATOR_FIELDS[field];
    const reading = check.readings.find((item) => item.field === sourceField);
    if (!reading || check.uncertainFields.includes(sourceField))
        return undefined;
    try {
        const value = hardwareReadingValue(sourceField, reading.value);
        return typeof value === 'number' ? { value, reading } : undefined;
    }
    catch {
        return undefined;
    }
}
export function inverterIsolatorOffers(input) {
    if (input.check.kind !== 'inverters')
        return [];
    const row = input.hardware.dcIsolators[input.isolatorIndex];
    if (!row || !dcIsolatorAcceptsInverterRatings(row))
        return [];
    return ['voltageV', 'currentA'].map((field) => {
        const sourceField = INVERTER_TO_DC_ISOLATOR_FIELDS[field];
        const source = inverterIsolatorReading(input.check, field);
        const uncertain = input.check.uncertainFields.includes(sourceField);
        const reading = source?.reading ?? input.check.readings.find((item) => item.field === sourceField);
        const needsConfirmation = uncertain || (field === 'currentA' && !source);
        const blocked = row[field] !== undefined
            ? 'existing'
            : input.isolatorLabelReadings?.some((item) => item.field === field)
                ? 'isolator-label'
                : undefined;
        const conflict = !!source &&
            (input.otherInverterChecks ?? []).some((other) => {
                const theirs = inverterIsolatorReading(other, field);
                return !!theirs && theirs.value !== source.value;
            });
        const name = field === 'voltageV' ? 'voltage (V)' : 'current (A)';
        return {
            field,
            sourceField,
            label: `DC isolator ${input.isolatorIndex + 1} ${name} — ${INVERTER_ISOLATOR_SOURCE_LABEL}`,
            reading,
            needsConfirmation,
            blocked,
            conflict,
            preTicked: !!source && !needsConfirmation && !blocked && !conflict,
        };
    });
}
export function selectedInverterIsolatorFields(offers, overrides) {
    return offers
        .filter((offer) => !!offer.reading &&
        !offer.blocked &&
        !offer.needsConfirmation &&
        (overrides[offer.field] ?? offer.preTicked))
        .map((offer) => offer.field);
}
export function inverterIsolatorOfferView(input) {
    const choices = input.check.kind === 'inverters' ? inverterIsolatorChoices(input.hardware) : [];
    const index = resolvedInverterIsolatorIndex(input.hardware, input.check, input.target);
    return {
        index,
        showPicker: input.check.kind === 'inverters' &&
            automaticInverterIsolatorIndex(input.hardware) === undefined,
        choices,
        offers: index === undefined
            ? []
            : inverterIsolatorOffers({
                hardware: input.hardware,
                check: input.check,
                isolatorIndex: index,
                isolatorLabelReadings: (input.dcIsolatorChecks ?? [])
                    .filter((item) => item.index === index)
                    .flatMap((item) => item.readings),
                ...(input.otherInverterChecks
                    ? { otherInverterChecks: input.otherInverterChecks }
                    : {}),
            }),
    };
}
/** Copy accepted inverter ratings onto empty Integrated or blank isolator fields. */
export function applyInverterIsolatorRatings(hardware, check, isolatorIndex, fields) {
    const next = FieldHardwareSchema.parse(hardware);
    const row = next.dcIsolators[isolatorIndex];
    if (!row)
        throw new Error('Hardware row no longer exists');
    if (!dcIsolatorAcceptsInverterRatings(row))
        throw new Error('DC isolator does not take inverter ratings');
    const applied = [];
    for (const field of fields) {
        if (row[field] !== undefined)
            continue;
        const source = inverterIsolatorReading(check, field);
        if (!source)
            continue;
        row[field] = source.value;
        applied.push(field);
    }
    return { hardware: FieldHardwareSchema.parse(next), applied };
}
//# sourceMappingURL=hardware-checks.js.map